import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { BookLink, Chapter, Reveal } from "./booking";

const IMAGES = [
  {
    src: "/images/haircut.jpg",
    alt: "Faded Barber Co. barber giving a precision clipper haircut in Springfield, VA",
    ratio: "aspect-[4/5]",
  },
  {
    src: "/images/kids.jpg",
    alt: "Faded Barber Co. barber with two smiling young clients after fresh cuts",
    ratio: "aspect-[4/3]",
  },
  {
    src: "/images/fade-closeup.jpg",
    alt: "Close-up of a Faded Barber Co. barber blending a skin fade with clippers",
    ratio: "aspect-[4/3]",
  },
  {
    src: "/images/shop-interior.webp",
    alt: "Inside Faded Barber Co. — barbers cutting hair at modern stations in Springfield, VA",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/images/storefront.jpg",
    alt: "Faded Barber Co. storefront sign on Backlick Road in Springfield, VA",
    ratio: "aspect-square",
  },
  {
    src: "/images/wall-logo.jpg",
    alt: "Faded Barber Co. logo wall sign inside the Springfield barbershop",
    ratio: "aspect-[4/5]",
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback(
    (dir) => setActive((a) => (a === null ? null : (a + dir + IMAGES.length) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, step]);

  return (
    <section id="gallery" data-testid="gallery-section" className="px-5 lg:px-12 py-20 lg:py-32 border-t border-border">
      <Chapter index="05" title="The Work" />
      <Reveal>
        <h2 className="font-display uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mb-14">
          Real cuts.
          <br />
          <span className="text-primary">Real shop.</span>
        </h2>
      </Reveal>

      <div className="columns-2 lg:columns-3 gap-4 [column-fill:balance]">
        {IMAGES.map((img, i) => (
          <Reveal key={img.src} delay={i * 0.06} className="mb-4 break-inside-avoid">
            <button
              data-testid={`gallery-image-${i}`}
              onClick={() => setActive(i)}
              className="block w-full overflow-hidden group relative"
              aria-label={`Open image: ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className={`w-full ${img.ratio} object-cover transition-transform duration-700 group-hover:scale-105`}
              />
              <span className="absolute inset-0 bg-background/0 group-hover:bg-background/20 transition-colors duration-500" aria-hidden="true" />
            </button>
          </Reveal>
        ))}
        <Reveal delay={0.2} className="mb-4 break-inside-avoid">
          <div className="bg-primary p-8 flex flex-col justify-between aspect-square">
            <p className="font-display uppercase text-3xl sm:text-4xl leading-[0.95] text-primary-foreground">
              Your cut could be next.
            </p>
            <BookLink
              testid="gallery-book-button"
              className="mt-6 inline-flex w-fit items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-primary-foreground border-b border-primary-foreground/50 pb-1 hover:border-primary-foreground transition-colors duration-300"
            >
              Book Now
            </BookLink>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              aria-label="Close image viewer"
              onClick={close}
              className="absolute top-5 right-5 p-3 text-foreground hover:text-primary transition-colors"
            >
              <X size={28} />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Previous image"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 sm:left-8 p-3 text-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              data-testid="lightbox-next"
              aria-label="Next image"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 sm:right-8 p-3 text-foreground hover:text-primary transition-colors"
            >
              <ChevronRight size={32} />
            </button>
            <motion.img
              key={IMAGES[active].src}
              src={IMAGES[active].src}
              alt={IMAGES[active].alt}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
